import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={15}
        className={i <= rating ? "fill-gold text-gold" : "text-white/20"}
      />
    ))}
  </div>
);

export const Testimonials = () => {
  const { t } = useLang();
  const reviews = t("reviews");

  return (
    <section className="relative z-10 py-24 border-t border-gold/10" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="label-eyebrow">{t("testimonials_eyebrow")}</p>
            <h2 className="mt-3 font-serif font-light text-4xl md:text-6xl text-white leading-none">
              {t("testimonials_t1")} <span className="text-gold-gradient italic">{t("testimonials_t2")}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <Stars rating={5} />
            <span className="text-sm text-white/55 font-light max-w-[16rem]">{t("testimonials_rating")}</span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`review-card-${i}`}
              className="relative h-full rounded-2xl border border-gold/15 bg-navy-800 p-7 hover:border-gold/40 transition-colors duration-500"
            >
              <Quote size={30} className="text-gold/25 mb-4" />
              <p className="text-white/75 font-light leading-relaxed">{r.text}</p>
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                <span className="grid place-items-center h-11 w-11 shrink-0 rounded-full bg-gold/15 border border-gold/30 font-serif text-lg text-gold">
                  {r.name.trim().charAt(0)}
                </span>
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{r.name}</p>
                  <p className="text-xs text-white/45 truncate">{r.location}</p>
                </div>
                <div className="ml-auto">
                  <Stars rating={r.rating} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
